import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check, Search } from 'lucide-react';

// Dropdown with tick boxes: value is an array; empty = all. Shows "Label (n)" when something is ticked.
export function TagMultiFilter({ label, allLabel, values, value, onChange, fmt = (v) => v, testid, search = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [shift, setShift] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('mousedown', away);
    window.addEventListener('keydown', esc, true);
    return () => { document.removeEventListener('mousedown', away); window.removeEventListener('keydown', esc, true); };
  }, [open]);
  const term = q.trim().toLowerCase();
  const list = term ? values.filter((v) => String(fmt(v)).toLowerCase().includes(term)) : values;
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const on = value.length > 0;
  return (
    <div ref={ref} className="relative" data-testid={testid}>
      <button type="button" onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setShift(Math.min(0, window.innerWidth - 8 - Math.min(256, window.innerWidth - 16) - r.left)); setOpen(!open); }} aria-expanded={open}
        className={`inline-flex h-9 max-w-[15rem] items-center gap-1.5 rounded-lg border px-2.5 text-sm transition-colors ${on ? 'border-[hsl(var(--ring))] bg-[hsl(var(--ring))]/5 font-medium text-foreground' : 'border-border bg-background text-foreground'}`} data-testid={`${testid}-button`}>
        <span className="truncate">{on ? (value.length === 1 ? fmt(value[0]) : `${label} (${value.length})`) : allLabel}</span>
        <ChevronDown size={14} className={`shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open ? (
        <div style={{ left: shift }} className={`absolute top-10 z-30 w-64 max-w-[calc(100vw-1rem)] rounded-xl border border-border bg-popover p-2 text-popover-foreground shadow-xl animate-in fade-in zoom-in-95 duration-100`} data-testid={`${testid}-menu`}>
          {search || values.length > 6 ? (
            <label className="relative mb-1.5 block"><Search size={13} className="absolute left-2 top-2.5 text-muted-foreground" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" autoFocus className="h-8 w-full rounded-lg border border-input bg-background pl-7 pr-2 text-xs" data-testid={`${testid}-search`} /></label>
          ) : null}
          <div className="mb-1 flex items-center justify-between px-1 text-[length:calc(var(--fs,1)*0.6875rem)]">
            <button type="button" onClick={() => onChange([...new Set([...value, ...list])])} className="font-medium text-muted-foreground hover:text-foreground" data-testid={`${testid}-all`}>Select all</button>
            <button type="button" onClick={() => onChange([])} disabled={!on} className="font-medium text-muted-foreground hover:text-foreground disabled:opacity-40" data-testid={`${testid}-clear`}>Clear</button>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {list.map((v) => {
              const ticked = value.includes(v);
              return (
                <button key={v} type="button" onClick={() => toggle(v)} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-secondary" data-testid={`${testid}-option`}>
                  <span className={`grid h-4 w-4 shrink-0 place-items-center rounded border ${ticked ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40'}`}>{ticked ? <Check size={11} /> : null}</span>
                  <span className="truncate">{fmt(v)}</span>
                </button>
              );
            })}
            {!list.length ? <p className="px-2 py-3 text-xs text-muted-foreground">Nothing to pick.</p> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
